import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import useCustomCollections from '@/hooks/useCustomCollections'
import { useNotifications } from '@/hooks/useNotifications'
import CustomCollectionsServiceNew from '@/services/CustomCollectionsServiceNew'
import { dispatchCollectionsChanged } from '@/lib/collectionsEvents'
import SearchActionsBar from '@/widgets/SearchActionsBar'
import CustomCollectionTable from './components/CustomCollectionTable'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import LoadMoreSection from '@/widgets/LoadMoreSection'

export default function CustomCollections() {
	const { t } = useTranslation()
	const [searchTerm, setSearchTerm] = useState('')
	const [selectedCollection, setSelectedCollection] = useState(null)
	const [isDeleting, setIsDeleting] = useState(false)

	const {
		customCollectionsData,
		loadingCustomCollections: loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadCustomCollectionsData,
		handleLoadMore,
		setError,
		setCustomCollectionsSorting,
		updateFilters,
		sortColumn,
		sortDirection,
	} = useCustomCollections()

	const { showDeleteConfirmation, showDeleteSuccess } = useNotifications({
		error,
		message: null,
		clearError: setError,
		clearMessage: () => {},
	})

	useEffect(() => {
		loadCustomCollectionsData()
	}, [loadCustomCollectionsData])

	useEffect(() => {
		const t = setTimeout(() => {
			if (searchTerm.length >= 3 || searchTerm.length === 0) {
				updateFilters({ searchText: searchTerm })
			}
		}, 500)
		return () => clearTimeout(t)
	}, [searchTerm, updateFilters])

	const handleSearchChange = useCallback(e => {
		setSearchTerm(e.target.value)
	}, [])

	const handleSelectCollection = useCallback(
		(collectionId, isSelected) => {
			if (isSelected) setSelectedCollection(collectionId)
			else if (selectedCollection === collectionId) setSelectedCollection(null)
		},
		[selectedCollection]
	)

	const handleDeleteSelected = useCallback(async () => {
		if (!selectedCollection) return
		const collectionToDelete = customCollectionsData.find(c => (c._id || c.id) === selectedCollection)
		const confirmed = await showDeleteConfirmation(
			1,
			t('customCollections.list.deleteDialog.confirmText', {
				name: collectionToDelete?.name || t('customCollections.list.deleteDialog.unknownName'),
			})
		)
		if (!confirmed) return
		setIsDeleting(true)
		try {
			const res = await CustomCollectionsServiceNew.deleteCustomCollection(selectedCollection)
			if (res.success) {
				setSelectedCollection(null)
				loadCustomCollectionsData()
				dispatchCollectionsChanged()
				showDeleteSuccess(1, t('customCollections.list.deleteDialog.deletedSuccess'))
			} else {
				setError(t('customCollections.list.deleteDialog.errorFallback'))
			}
		} catch (e) {
			setError(e.message || t('customCollections.list.deleteDialog.errorFallback'))
		} finally {
			setIsDeleting(false)
		}
	}, [
		selectedCollection,
		customCollectionsData,
		loadCustomCollectionsData,
		showDeleteConfirmation,
		showDeleteSuccess,
		setError,
		t,
	])

	const handleSort = useCallback(
		column => {
			const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
			setCustomCollectionsSorting(column, newDirection)
		},
		[sortColumn, sortDirection, setCustomCollectionsSorting]
	)

	if (loading && customCollectionsData.length === 0) {
		return <LoadingIndicator />
	}

	const buttons = [
		{
			type: 'delete',
			label: t('customCollections.list.actions.delete'),
			count: selectedCollection ? 1 : 0,
			onClick: handleDeleteSelected,
			loading: isDeleting,
		},
		{ type: 'add', label: t('customCollections.list.actions.addNew'), to: '/custom-collections/add' },
	]

	return (
		<div className="container mx-auto px-4 py-6">
			<div className="mb-6">
				<h1 className="text-3xl font-bold text-gray-900">{t('customCollections.list.title')}</h1>
				<p className="mt-2 text-gray-600">{t('customCollections.list.subtitle', { total: totalCount })}</p>
			</div>

			<SearchActionsBar
				value={searchTerm}
				onChange={handleSearchChange}
				placeholder={t('customCollections.list.searchPlaceholder')}
				buttons={buttons}
			/>

			<CustomCollectionTable
				customCollectionsData={customCollectionsData}
				loading={loading}
				selectedCollection={selectedCollection}
				onSelectCollection={handleSelectCollection}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
			/>

			<LoadMoreSection
				loading={loadingMore}
				hasMore={hasMore}
				onLoadMore={handleLoadMore}
				dataLength={customCollectionsData.length}
				totalCount={totalCount}
			/>
		</div>
	)
}
